import {
    Button,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    makeStyles,
    mergeClasses,
    OverlayDrawer,
    tokens,
} from '@fluentui/react-components';
import { ArrowEnter20Regular, ArrowFit20Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cometDarkTheme } from '../../theme';
import FilterActionBar from '../filters/filter-actions/FilterActionBar';

export interface IToggleWidthDrawerProps {
    primaryButtonDisabled: boolean;
    isOpen: boolean;
    onClose?: () => void;
    onResize?: () => void;
    onConfirm?: () => void;
    children: React.ReactNode;
    headerText: string;
}

const useStyles = makeStyles({
    drawer: {
        '--drilldownpanel-component-vertical-gap': tokens.spacingVerticalM, // 12px
        transitionProperty: 'width',
        transitionDuration: '500ms',
        borderLeft: 'none',
    },
    drawerCollapsed: {
        width: '700px',
    },
    drawerExpanded: {
        width: '1186px',
    },
    drawerHeader: {
        backgroundColor: tokens.colorBrandBackground,
        height: '48px',
        minHeight: '48px',
        maxHeight: '48px',
        paddingTop: '5px',
        paddingBottom: 0,
        paddingLeft: tokens.spacingHorizontalXXL, // 24px
        paddingRight: tokens.spacingHorizontalXL, // 20px
        display: 'flex',
    },
    headerTitleContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        marginLeft: 0,
        gap: tokens.spacingHorizontalMNudge, // 10px
    },
    headerTitle: {
        color: tokens.colorNeutralForegroundOnBrand,
        margin: 0,
        lineHeight: '24px',
        fontSize: '18px',
        fontWeight: tokens.fontWeightSemibold,
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: '2px',
        gap: tokens.spacingHorizontalMNudge, // 10px
    },
    expandButton: {
        backgroundColor: 'transparent',
        borderTopColor: cometDarkTheme.colorNeutralStroke1,
        borderRightColor: cometDarkTheme.colorNeutralStroke1,
        borderBottomColor: cometDarkTheme.colorNeutralStroke1,
        borderLeftColor: cometDarkTheme.colorNeutralStroke1,
        color: cometDarkTheme.colorNeutralForeground1,
        ':hover': {
            backgroundColor: 'transparent',
            borderTopColor: cometDarkTheme.colorNeutralStroke1Hover,
            borderRightColor: cometDarkTheme.colorNeutralStroke1Hover,
            borderBottomColor: cometDarkTheme.colorNeutralStroke1Hover,
            borderLeftColor: cometDarkTheme.colorNeutralStroke1Hover,
            color: cometDarkTheme.colorNeutralForeground1Hover,
        },
        ':active': {
            backgroundColor: 'transparent',
        },
    },
    closeButton: {
        color: cometDarkTheme.colorNeutralForeground1,
        width: tokens.spacingHorizontalXXL, // 24px
        height: tokens.spacingVerticalXXL, // 24px
        minWidth: tokens.spacingHorizontalXXL, // 24px
        padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXXS}`, // 2px
        ':hover': {
            backgroundColor: cometDarkTheme.colorNeutralBackground2Hover,
            color: cometDarkTheme.colorBrandForeground2Hover,
        },
        ':active': {
            backgroundColor: cometDarkTheme.colorNeutralBackground2Pressed,
        },
    },
    drawerBody: {
        paddingTop: tokens.spacingVerticalM, // 12px
        paddingRight: '14px',
        paddingBottom: 0,
        paddingLeft: '14px',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        overflowX: 'hidden',
        gap: 'var(--drilldownpanel-component-vertical-gap)',
    },
    footer: {
        boxShadow: '0px -5px 5px -5px #00000040',
        width: '100%',
        marginTop: 'auto',
    },
});

export const ToggleWidthDrawer = (props: IToggleWidthDrawerProps) => {
    const { t } = useTranslation();
    const styles = useStyles();

    const [panelIsExpanded, setPanelIsExpanded] = useState(false);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const panelSizeToggleButtonText = t(panelIsExpanded ? 'reducePanelSize' : 'expandPanelSize').toString();
    const ExpandIcon = panelIsExpanded ? ArrowEnter20Regular : ArrowFit20Regular;

    const onExpandButtonClick = () => {
        setPanelIsExpanded(!panelIsExpanded);
        if (props.onResize) {
            props.onResize();
        }
    };

    const handleOpenChange = (_: unknown, data: { open: boolean }) => {
        if (!data.open && props.onClose) {
            props.onClose();
        }
    };

    const drawerClassName = mergeClasses(styles.drawer, panelIsExpanded ? styles.drawerExpanded : styles.drawerCollapsed);

    useEffect(() => {
        if (props.isOpen) {
            requestAnimationFrame(() => closeButtonRef.current?.focus());
        }
    }, [props.isOpen]);

    return (
        <OverlayDrawer
            open={props.isOpen}
            onOpenChange={handleOpenChange}
            position='end'
            modalType='modal'
            className={drawerClassName}
            data-testid='drilldownDrawerTestId'
        >
            <DrawerHeader className={styles.drawerHeader}>
                <DrawerHeaderTitle
                    action={
                        <div className={styles.headerActions}>
                            <Button
                                id='expandButton'
                                appearance='outline'
                                icon={<ExpandIcon />}
                                onClick={onExpandButtonClick}
                                aria-pressed={panelIsExpanded}
                                className={styles.expandButton}
                                aria-label={panelSizeToggleButtonText}
                            >
                                {panelSizeToggleButtonText}
                            </Button>
                            <Button
                                ref={closeButtonRef}
                                appearance='subtle'
                                aria-label={t('close').toString()}
                                icon={<Dismiss24Regular />}
                                onClick={props.onClose}
                                className={styles.closeButton}
                            />
                        </div>
                    }
                >
                    <span className={styles.headerTitle}>{props.headerText}</span>
                </DrawerHeaderTitle>
            </DrawerHeader>

            <DrawerBody className={styles.drawerBody}>{props.children}</DrawerBody>

            <div className={styles.footer}>
                <FilterActionBar
                    primaryButtonLabel='save'
                    secondaryButtonLabel='cancel'
                    primaryHandler={props.onConfirm!}
                    secondaryHandler={props.onClose!}
                    primaryButtonDisabled={props.primaryButtonDisabled}
                />
            </div>
        </OverlayDrawer>
    );
};
